import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls, Field, StatusPill } from "@/components/AdminTools";
import ScreenerPreview from "@/components/ScreenerPreview";
import { Plus, Trash2, Save, X, ListChecks, Eye } from "lucide-react";

const CATEGORIES = ["sleep", "metabolic", "hormone", "diagnostics", "wellness", "other"];
const STATUSES = ["active", "draft", "archived"];
const QTYPES = ["single", "multi", "text"];

const EMPTY = { name: "", category: "sleep", intro: "", questions: [], recommended_package_ids: [], status: "active" };

export default function AdminScreeners() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([api.get("/screeners"), api.get("/packages")]);
      setItems(s.data || []); setPackages(p.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (it) => {
    setEditing(it.id);
    // Ensure every question carries a stable id for React keys (older
    // screeners may have been saved without one).
    const questions = (it.questions || []).map((q) =>
      q && q.id ? q : { ...q, id: Math.random().toString(36).slice(2, 9) },
    );
    setForm({ ...EMPTY, ...it, questions, recommended_package_ids: it.recommended_package_ids || [] });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/screeners", form);
      else await api.patch(`/screeners/${editing}`, form);
      toast.success("Saved."); cancel(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this screener?")) return;
    try { await api.delete(`/screeners/${id}`); load(); toast.success("Deleted."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, { id: Math.random().toString(36).slice(2,7), prompt: "", type: "single", options: ["Yes","No"] }] }));
  const updQ = (idx, patch) => setForm((f) => ({ ...f, questions: f.questions.map((q,i) => i===idx ? { ...q, ...patch } : q) }));
  const removeQ = (idx) => setForm((f) => ({ ...f, questions: f.questions.filter((_,i)=>i!==idx) }));

  const togglePkg = (pid) => setForm((f) => ({
    ...f,
    recommended_package_ids: f.recommended_package_ids.includes(pid) ? f.recommended_package_ids.filter(x=>x!==pid) : [...f.recommended_package_ids, pid],
  }));

  return (
    <ToolPageShell
      eyebrow="Admin · Catalog"
      title="Screener templates"
      intro="Author screening flows clinics can deploy as branded landing pages. Submissions auto-recommend matching packages."
      actions={<button data-testid="screeners-new-btn" onClick={startNew} className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"><Plus className="w-3.5 h-3.5" /> New</button>}
    >
      {editing && (
        <div data-testid="screeners-form" className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" required><input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className={inputCls} /></Field>
            <Field label="Category">
              <select value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} className={inputCls}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Intro / context"><textarea rows={3} value={form.intro||""} onChange={(e)=>setForm({...form,intro:e.target.value})} className={`${inputCls} resize-y`} /></Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Questions</div>
              <button onClick={addQ} className="text-xs font-semibold text-blue-700 hover:text-blue-900">+ Add question</button>
            </div>
            <div className="space-y-3">
              {form.questions.map((q, i) => (
                <div key={q.id || `q-idx-${i}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input value={q.prompt} onChange={(e)=>updQ(i,{prompt:e.target.value})} placeholder="Question prompt" className={`${inputCls} flex-1`} />
                    <select value={q.type} onChange={(e)=>updQ(i,{type:e.target.value})} className={inputCls + " w-32"}>
                      {QTYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button onClick={()=>removeQ(i)} className="text-red-600 hover:text-red-800"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {q.type !== "text" && (
                    <input value={(q.options||[]).join(", ")} onChange={(e)=>updQ(i,{options:e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})}
                      placeholder="Options, comma-separated" className={inputCls} />
                  )}
                </div>
              ))}
              {form.questions.length === 0 && <div className="text-xs text-slate-500">No questions yet.</div>}
            </div>
          </div>

          <Field label="Recommend these packages">
            <div className="flex flex-wrap gap-2">
              {packages.map(p => {
                const active = form.recommended_package_ids.includes(p.id);
                return (
                  <button type="button" key={p.id} onClick={()=>togglePkg(p.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
                    {p.name}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Status">
            <select value={form.status} onChange={(e)=>setForm({...form,status:e.target.value})} className={inputCls + " sm:w-48"}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>

          <div className="flex items-center gap-2 pt-2">
            <button onClick={save} className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"><Save className="w-3.5 h-3.5" /> Save</button>
            <button onClick={cancel} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"><X className="w-3.5 h-3.5" /> Cancel</button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((s) => (
          <div key={s.id} data-testid={`screener-card-${s.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600 capitalize">{s.category}</div>
                <h3 className="mt-1 font-display text-lg font-semibold text-slate-900">{s.name}</h3>
                <p className="mt-1 text-sm text-slate-600 line-clamp-2">{s.intro}</p>
              </div>
              <StatusPill status={s.status} />
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-600">
              <ListChecks className="w-3.5 h-3.5" /> {s.questions?.length || 0} questions · {s.recommended_package_ids?.length || 0} recommended pkgs
            </div>
            <div className="mt-5 flex items-center gap-2">
              <button
                onClick={() => setPreviewing(s)}
                data-testid={`screener-preview-${s.id}`}
                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
              >
                <Eye className="w-3 h-3" /> Preview
              </button>
              <button onClick={()=>startEdit(s)} className="text-xs font-semibold text-slate-700 hover:text-slate-900">Edit</button>
              <button onClick={()=>remove(s.id)} className="inline-flex items-center text-xs text-red-600 hover:text-red-800 ml-auto"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="sm:col-span-2 text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">No screeners yet.</div>}
      </div>

      {previewing && (
        <ScreenerPreview screener={previewing} clinicName="Preview clinic" onClose={() => setPreviewing(null)} />
      )}
    </ToolPageShell>
  );
}
